import React from 'react';
import { useTreatments, SplitContext } from '@splitsoftware/splitio-react';
import { Permission } from 'Generated/schema';
import { SPLIT_FLAGS } from 'Source/constants';
import { useGetPantherAIEnabled } from 'Source/graphql/queries/getPantherAIEnabled.generated';
import useCheckPermissions from './useCheckPermissions';

/**
 * hook that returns whether AI is enabled for a given component, along with a
 * loading flag indicating whether the underlying `pantherAIEnabled` query is
 * still in-flight. Use this variant when the caller must distinguish between
 * "AI is disabled" and "we don't know yet" (e.g. landing-page redirects, where
 * a premature decision would flash the wrong page).
 */
export const useAIEnabledWithLoading = (
  featureFlags: (keyof typeof SPLIT_FLAGS)[] = [],
): { enabled: boolean; loading: boolean } => {
  const userHasRunPantherAIPermission = useCheckPermissions(Permission.RunPantherAi);
  const { data, loading } = useGetPantherAIEnabled({ skip: !userHasRunPantherAIPermission });
  // Treatments read 'control' (off) until the Split SDK is ready, so gate loading
  // on readiness — else callers act on a premature "disabled" (tab reflow, wrong landing page).
  const { isReady, isReadyFromCache } = React.useContext(SplitContext);
  const toggleNames = React.useMemo(
    () => featureFlags.map(flagKey => SPLIT_FLAGS[flagKey] ?? ''),
    [featureFlags],
  );
  const treatments = useTreatments(toggleNames);
  const allFeatureFlagsEnabled = React.useMemo(() => {
    let allEnabled = true;
    toggleNames.forEach(name => {
      const { treatment } = treatments[name] ?? { treatment: 'off' };
      if (treatment !== 'on') {
        allEnabled = false;
      }
    });
    return allEnabled;
  }, [treatments, toggleNames]);

  const pantherAIEnabled = data?.pantherAIEnabled ?? false;
  // When flags are requested, the answer isn't conclusive until Split resolves
  // their treatments (ready, or ready-from-cache for a warm LOCALSTORAGE start).
  const featureFlagsLoading = toggleNames.length > 0 && !isReady && !isReadyFromCache;
  return {
    enabled: userHasRunPantherAIPermission && pantherAIEnabled && allFeatureFlagsEnabled,
    // When the user lacks the permission we skip the query, so loading is
    // irrelevant — the result is conclusively "disabled".
    loading: userHasRunPantherAIPermission && (loading || featureFlagsLoading),
  };
};

/**
 * hook that returns whether AI is enabled for a given component.
 * This hook always asserts:
 * - AI is disabled if the global pantherAIEnabled setting is off
 * - AI is disabled if the user does not have the RunPantherAI permission
 * in addition to those conditions, individual AI components are gated by their respective feature flags
 * and so we also check that the appropriate feature flags are all ON. if featureFlags is an empty array,
 * this check is skipped.
 */
const useAIEnabled = (featureFlags: (keyof typeof SPLIT_FLAGS)[] = []): boolean => {
  return useAIEnabledWithLoading(featureFlags).enabled;
};

export default useAIEnabled;
